import { pickOnPrimaryColor } from '@/themes/colorUtils';
import { opaqueContrastBaseOf } from '@/themes/surfaceOpacity';

/**
 * WEB-1684 — оформление подписи-пилюли поверх фотографии (блок «Картинка»).
 *
 * До этой правки цвет текста, кегль и фон пилюли были зашиты прямо в ветке
 * рендера, и владелец не мог покрасить подпись ничем: поля «внутри элемента»
 * до неё не доходят, а собственный `fontSize: 13` перебивал любой
 * унаследованный кегль.
 *
 * ЗАШИТО БЫЛО НАМЕРЕННО. Подпись лежит поверх ПРОИЗВОЛЬНОГО снимка: заранее
 * неизвестно, светлый он или тёмный, а тёмная полупрозрачная плашка с белым
 * текстом читается на любом. Отдать цвета автору «как есть» значит вернуть
 * класс дефекта, с которым в проекте уже боролись (WEB-872, WEB-966,
 * WEB-1009) — светлый текст на светлом фото.
 *
 * ПОЭТОМУ СВОБОДА СО СТРАХОВКОЙ, три правила:
 *  1. пусто — ровно прежний вид (те же переменные, те же 13px): ни одна
 *     воронка, созданная до правки, не сдвигается ни на пиксель;
 *  2. задан ТОЛЬКО фон — цвет текста доклеивается авто-контрастом по этому
 *     фону (`pickOnPrimaryColor`, тот же помощник, что у поверхности элемента,
 *     blockInnerSurface.tsx), иначе светлая пилюля получила бы белый текст;
 *  3. задан цвет текста — он сильнее авто-контраста: это осознанный выбор
 *     автора, и подменять его догадкой нельзя.
 *
 * ОТДЕЛЬНОЕ РЕШЕНИЕ ПРО ПОЛУПРОЗРАЧНЫЙ ФОН. Авто-контраст считается по
 * ЯРКОСТИ ЗАЛИВКИ, а сквозь полупрозрачную заливку виден сам снимок —
 * яркость такой заливки не предсказывает, что окажется под текстом. Для неё
 * авто-контраст не включается: остаётся прежний светлый текст, единственный
 * безопасный над любым фото. Порог — ОБЩИЙ на все помощники
 * (`OPAQUE_ENOUGH_ALPHA`, themes/surfaceOpacity.ts); своего здесь нет с
 * WEB-1859, до неё тут была половина непрозрачности, а у помощника экрана 0.9.
 */

/** Прежний вид, зашитый в рендере до WEB-1684. Пусто = ровно эти значения. */
export const CAPTION_CHIP_DEFAULT_BACKGROUND =
  'var(--caption-chip-bg, rgba(0, 0, 0, 0.62))';
/**
 * Инвариант WEB-872: цвет через переменную, а не голый `#fff`. Дефолт белый
 * осознанно — подложка чипа по умолчанию всегда тёмная.
 */
export const CAPTION_CHIP_DEFAULT_TEXT_COLOR = 'var(--caption-chip-fg, #fff)';
export const CAPTION_CHIP_DEFAULT_FONT_SIZE = 13;

export interface CaptionChipAppearanceFields {
  /** Цвет текста подписи. Absent = переменная темы (прежний белый). */
  captionChipTextColor?: string;
  /** Цвет фона пилюли. Absent = прежняя тёмная полупрозрачная плашка. */
  captionChipBackgroundColor?: string;
  /** Кегль подписи в px. Absent = прежние 13. */
  captionChipFontSize?: number;
}

export interface CaptionChipAppearance {
  background: string;
  color: string;
  fontSize: number;
}

/**
 * Код цвета без альфа-канала — либо null, если по заливке цвет текста считать
 * нельзя (`var(...)` и прочее неизмеримое, либо плёнка ниже порога).
 *
 * WEB-1699 (второй возврат 02.09): помощник ЭКСПОРТИРОВАЛСЯ ради кнопки
 * варианта в попапе лоадера (PublicBlockRendererExtended) — чтобы её точка
 * получила единый порог без правки её файла.
 *
 * WEB-1859: собственного замера здесь БОЛЬШЕ НЕТ — он делегирован общему
 * `opaqueContrastBaseOf` (themes/surfaceOpacity.ts), где порог один на все
 * помощники. Два следствия той правки: порог поднялся с 0x80 до 0.9, и стали
 * измеримыми записи, которые прежний разбор отбрасывал (`rgba()`, `hsl()`,
 * имена цветов).
 *
 * WEB-1711 (возврат 02.09): попап лоадера с тех пор зовёт общий помощник
 * НАПРЯМУЮ — переходник свою задачу отслужил, у него остался ровно один
 * читатель, здешний. Экспорт оставлен (снимать — правка чужой карточки), но
 * ЖДАТЬ по нему второго конца больше не надо: единственный порог живёт в
 * themes/surfaceOpacity.ts и сверяется сторожем `surfaceOpacityEnds.web1859`.
 */
export function opaqueHexOf(color: string): string | null {
  return opaqueContrastBaseOf(color);
}

/** Итоговые фон, цвет текста и кегль подписи-пилюли. */
export function captionChipAppearance(
  fields: CaptionChipAppearanceFields,
): CaptionChipAppearance {
  const fill = fields.captionChipBackgroundColor;
  const hasFill = typeof fill === 'string' && fill.trim().length > 0;

  const authorText = fields.captionChipTextColor;
  const hasAuthorText =
    typeof authorText === 'string' && authorText.trim().length > 0;

  // Свой цвет сильнее авто-контраста; авто-контраст сильнее прежнего дефолта —
  // и только по НЕПРОЗРАЧНОЙ заливке (см. шапку файла).
  const autoText =
    !hasAuthorText && hasFill ? opaqueHexOf(String(fill)) : null;

  return {
    background: hasFill ? String(fill) : CAPTION_CHIP_DEFAULT_BACKGROUND,
    color: hasAuthorText
      ? String(authorText)
      : autoText
        ? pickOnPrimaryColor(autoText)
        : CAPTION_CHIP_DEFAULT_TEXT_COLOR,
    fontSize: fields.captionChipFontSize ?? CAPTION_CHIP_DEFAULT_FONT_SIZE,
  };
}
